import { useRef } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { TPickDateAction } from '@/shared/model';
import { useReportStore } from '@/entities/report';
import type { EventRangePickersProps } from './eventRangePickers';

/**
 * Drives the paired start and end date-time pickers used when adjusting an ECG event's range on the beats tab. Holds DOM anchors for the four jump and pick buttons, relays picker changes to the parent, and writes the ECG jump target, jump portion and pick-on-chart mode into the tab-scoped report store.
 *
 * @param props - Picker props carrying the onChangePicker callback.
 * @returns An object carrying the four ref anchors plus the onChange, onClickJumpTo and onClickPickOn handlers.
 */
const useEventRangePickers = ({
  onChangePicker,
}: Pick<EventRangePickersProps, 'onChangePicker'>) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Writes the beats tab's ECG jump target back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement jump timestamp, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Writes the beats tab's pick-on-chart mode back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement pick-date action, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Writes the beats tab's jump portion back into the report store, which decides where in the viewport the jump target lands. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement portion index, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const jumpStartRef = useRef<HTMLDivElement>(null);
  const jumpEndRef = useRef<HTMLDivElement>(null);
  const pickStartRef = useRef<HTMLDivElement>(null);
  const pickEndRef = useRef<HTMLDivElement>(null);

  /**
   * Relays a start or end picker change up to the owning event modal. Passes the field name, moment and formatted text straight to the onChangePicker prop without touching any local state.
   *
   * @param name - Boundary field the picker writes to, such as startDate or endTime.
   * @param time - Picked moment value for that field.
   * @param timeString - Formatted text emitted by the picker.
   * @returns Nothing.
   */
  const onChange = (name: string, time: any, timeString?: string | string[]) => {
    onChangePicker(name, time, timeString);
  };

  /**
   * Moves the ECG viewer to one end of the event range. Converts the date to epoch milliseconds, writes it as the jump target only when it differs from the current one, and aligns the view by setting the jump portion to 0 for the start boundary or 2 for the end.
   *
   * @param date - Boundary date the ECG viewer should jump to.
   * @param isStart - True for the range's start boundary, false for its end.
   * @returns Nothing.
   */
  const onClickJumpTo = (date: any, isStart: boolean) => {
    const epochDate = new Date(date).getTime();
    setJumpDate((prev: any) => {
      if (epochDate && epochDate !== prev) {
        return epochDate;
      }
      return prev;
    });
    setJumpPortion(isStart ? 0 : 2);
  };

  /**
   * Arms chart picking so the reviewer's next ECG click sets one end of the event range. Sets the tab's pick-date action to the start or end mode.
   *
   * @param isStart - True to pick the range's start, false to pick its end.
   * @returns Nothing.
   */
  const onClickPickOn = (isStart: boolean) => {
    setPickDateAction(isStart ? TPickDateAction.START : TPickDateAction.END);
  };

  return {
    jumpStartRef,
    jumpEndRef,
    pickStartRef,
    pickEndRef,
    onChange,
    onClickJumpTo,
    onClickPickOn,
  };
};

export default useEventRangePickers;
